// /settings/language — отдельный раздел «Язык» (как в IEC Hero): язык
// интерфейса + язык, на котором AI генерирует материалы.
//
// Раньше оба селектора жили внутри «Внешнего вида» — там их искали в последнюю
// очередь. Язык интерфейса персистится единым хуком usePersistLocale
// (i18next + localStorage + users.language), язык генерации — PowerSync-UPDATE
// по users.answer_locale (rules/offline-sync.md §13, §18).

import { Combobox } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconLanguage } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsFieldRow, SettingsSection } from "@/components/settings/settings-shell";
import { usePersistLocale } from "@/hooks/use-persist-locale";
import { useSession } from "@/hooks/use-session";
import { useSupportedLocales } from "@/hooks/use-supported-locales";
import { toLocaleComboboxOption } from "@/i18n/locale-flags";
import type { SupportedLocale } from "@/i18n/locales";
import { useLocale } from "@/i18n/use-locale";
import { getPowerSync } from "@/lib/powersync/client";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

interface LocalUserLanguageRow {
	answer_locale: string | null;
}

// Sentinel-значения опций языка генерации (UI ↔ users.answer_locale):
//   ANSWER_SAME_AS_UI → NULL     (язык интерфейса, дефолт)
//   ANSWER_SOURCE     → 'source' (язык исходного материала)
//   ISO-код           → ISO-код  (конкретный язык)
const ANSWER_SAME_AS_UI = "__same__";
const ANSWER_SOURCE = "__source__";

/** `users.answer_locale` (БД) → значение селектора. */
function answerLocaleToOption(v: string | null): string {
	if (v === "source") return ANSWER_SOURCE;
	return v ?? ANSWER_SAME_AS_UI;
}
/** Значение селектора → `users.answer_locale` (БД). */
function optionToAnswerLocale(v: string): string | null {
	if (v === ANSWER_SAME_AS_UI) return null;
	if (v === ANSWER_SOURCE) return "source";
	return v;
}

export const Route = createFileRoute("/settings/language")({
	component: LanguageRoute,
});

function LanguageRoute() {
	const { t } = useTranslation("settings");
	const { locale } = useLocale();
	// Единый персист языка (i18next + localStorage + users.language в БД) —
	// тот же хук, что и переключатели в шапке, чтобы UI и серверный контент
	// (рекомендации/уведомления) не расходились.
	const persistLocale = usePersistLocale();
	const { user: me } = useSession();

	const localUser = usePowerSyncQuery<LocalUserLanguageRow>(
		`SELECT answer_locale FROM users WHERE id = ? LIMIT 1`,
		me?.id ? [me.id] : [],
	);
	const localRow = localUser.data?.[0];

	// `__same__` → answer_locale=NULL («как язык интерфейса»).
	const [answerLocale, setAnswerLocale] = useState<string>(ANSWER_SAME_AS_UI);
	const hydrated = useRef(false);

	useEffect(() => {
		if (!localRow || hydrated.current) return;
		hydrated.current = true;
		setAnswerLocale(answerLocaleToOption(localRow.answer_locale));
	}, [localRow]);

	const { locales: supportedLocales } = useSupportedLocales();
	const localeOptions = supportedLocales.map(toLocaleComboboxOption);
	// Meta-опции («язык интерфейса» / «язык материала») — без флагов.
	const answerLocaleOptions = [
		{ value: ANSWER_SAME_AS_UI, label: t("language.answerSameAsUi") },
		{ value: ANSWER_SOURCE, label: t("language.answerSourceLang") },
		...localeOptions,
	];

	function persistAnswerLocale(next: string) {
		setAnswerLocale(next);
		if (!me?.id) return;
		// UI-sentinel → значение users.answer_locale (NULL / 'source' / ISO).
		const value = optionToAnswerLocale(next);
		void getPowerSync()
			.execute(`UPDATE users SET answer_locale = ?, updated_at = ? WHERE id = ?`, [
				value,
				new Date().toISOString(),
				me.id,
			])
			.then(() => toast.success(t("savedJust")))
			.catch(() => toast.error(t("language.saveError")));
	}

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("language.title")}
				description={t("language.subtitle")}
				icon={<IconLanguage stroke={1.75} />}
			/>

			<SettingsSection title={t("language.section")} icon={<IconLanguage stroke={1.75} />}>
				<SettingsFieldRow label={t("language.ui")} description={t("language.uiHelp")}>
					<Combobox
						options={localeOptions}
						value={locale}
						onChange={(v) => void persistLocale(v as SupportedLocale)}
						placeholder={t("language.ui")}
					/>
				</SettingsFieldRow>
				<SettingsFieldRow
					label={t("language.generation")}
					description={t("language.generationHelp")}
				>
					<Combobox
						options={answerLocaleOptions}
						value={answerLocale}
						onChange={persistAnswerLocale}
						placeholder={t("language.generation")}
					/>
				</SettingsFieldRow>
			</SettingsSection>
		</SettingsPageContainer>
	);
}
